import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import Iconify from 'src/components/Iconify';
import { useDispatch, useSelector } from 'src/redux/store';
import { changeOpenDetalhesChamado, getListaColunas, getListaTickets, getVerChamado } from 'src/redux/slices/chamados';
import { getListaUsuariosTransferencia } from 'src/redux/slices/user';
import useAuth from 'src/hooks/useAuth';
import useNovoChamado from 'src/hooks/useNovoChamado';
import useTabs from 'src/hooks/useTabs';
import useTable from 'src/hooks/useTable';
import actionListaTicketsPorUsuario from 'src/actions/chamados/actionListaTicketsPorUsuario';
import { NovoChamado } from 'src/sections/@dashboard/tickets/ChamadosNovoChamado';
import ChamadosHeader from 'src/sections/@dashboard/chamados/ChamadosHeader';
import ChamadosRowTableNew from 'src/sections/@dashboard/chamados/ChamadosRowTableNew';
import GenericTable from 'src/components/table/GenericTable';
import { CHAMADOS_DESIGN } from 'src/sections/@dashboard/chamados/Chamados.design';
import MenuDatas2 from 'src/components/MenuDatas2';

const D = CHAMADOS_DESIGN;

const STATUS_OPTIONS = [
  { value: null, label: 'Todos' },
  { value: 'aberto', label: 'Abertos' },
  { value: 'pendente', label: 'Pendentes' },
  { value: 'concluido', label: 'Concluidos' },
  { value: 'recusado', label: 'Recusados' },
  { value: 'cancelado', label: 'Cancelados' },
];

const TABLE_HEAD = [
  { id: 'chamado', label: 'Chamado', className: 'w-[140px]' },
  { id: 'assunto', label: 'Assunto', className: 'flex-1 min-w-[200px] pr-4' },
  { id: 'status', label: 'Status', className: 'w-[120px]' },
  { id: 'data_criacao', label: 'Data Criacao', className: 'w-[150px]' },
  { id: 'data_conclusao', label: 'Data Conclusao', className: 'w-[150px]' },
  { id: 'ultima_modificacao', label: 'Ultima Modificacao', className: 'w-[150px]' },
  { id: 'nome_autor', label: 'Criado por', className: 'w-[120px]' },
  { id: 'completado', label: 'Completado', className: 'w-[90px]' },
  { id: 'actions', label: '', className: 'w-[40px]' },
];

export default function MeusChamados() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useAuth();
  const { novoChamadoProps, handleOpenNovoChamado } = useNovoChamado();
  const { listaUsuariosTransferencia } = useSelector((state) => state.user);

  const { page, rowsPerPage, setPage, onChangePage, onChangeRowsPerPage } = useTable({ defaultOrderBy: 'date_created', defaultRowsPerPage: 25 });
  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs(null);

  const [filterPesquisa, setFilterPesquisa] = useState('');
  const [isPesquisado, setIsPesquisado] = useState(false);
  const [inicio, setInicio] = useState(startOfMonth(new Date()));
  const [final, setFinal] = useState(endOfMonth(new Date()));
  const [valorEscolhidoUsuario, setValorEscolhidoUsuario] = useState('');
  const [listaPesquisa, setListaPesquisa] = useState([]);
  const [listaFiltro, setListaFiltro] = useState([]);

  const inicioRef = useRef(inicio);
  const finalRef = useRef(final);
  inicioRef.current = inicio;
  finalRef.current = final;

  const tableData = isPesquisado ? listaPesquisa : listaFiltro;

  const buscarChamados = useCallback(async () => {
    try {
      const inicio2 = format(inicioRef.current, 'yyyy-MM-dd');
      const final2 = format(finalRef.current, 'yyyy-MM-dd');
      const querys = `?periodo=null&cliente_id=${user.clienteId}&usuario_id=${user.id}&inicio=${inicio2}&final=${final2}&action=null&search=${filterPesquisa}`;
      const response = await actionListaTicketsPorUsuario(querys);
      setListaFiltro(response.data.data);
    } catch (error) {
      console.log(error);
    }
  }, [user.clienteId, user.id, filterPesquisa]);

  async function buscarChamadosPesquisa() {
    if (!filterPesquisa) return;
    try {
      const inicio2 = format(inicioRef.current, 'yyyy-MM-dd');
      const final2 = format(finalRef.current, 'yyyy-MM-dd');
      const querys = `?periodo=pesquisar&cliente_id=${user.clienteId}&usuario_id=${user.id}&inicio=${inicio2}&final=${final2}&action=pesquisar&search=${filterPesquisa}`;
      const response = await actionListaTicketsPorUsuario(querys);
      setListaPesquisa(response.data.data);
      setIsPesquisado(true);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    dispatch(getListaUsuariosTransferencia(user.clienteId));
    dispatch(getListaColunas({ setorId: user.setorId, usuarioId: user.id, filial: '' }));
    dispatch(getListaTickets({ setorId: user.setorId, usuarioId: user.id, filial: '' }));
  }, [location]);

  async function handleOpenDetails(card) {
    dispatch(getVerChamado(card._id));
    dispatch(changeOpenDetalhesChamado(true));
  }

  const onPesquisar = () => {
    buscarChamadosPesquisa();
  };

  const dataFiltered = applySortFilter({ tableData, filterStatus, valorEscolhidoUsuario });

  return (
    <div className={D.page.container}>
      <ChamadosHeader onCriarChamado={handleOpenNovoChamado} />

      {/* Filters */}
      <div className="px-6 py-3 bg-white border-b border-gray-100 flex items-center gap-3 flex-wrap">
        <MenuDatas2
          inicial={inicio}
          final={final}
          changeInicial={(newValue) => setInicio(newValue)}
          changeFinal={(newValue) => setFinal(newValue)}
          onPesquisar={buscarChamados}
        />

        <div className="min-w-[200px]">
          <Select value={valorEscolhidoUsuario || undefined} onValueChange={(value) => setValorEscolhidoUsuario(value === 'all' ? '' : value)}>
            <SelectTrigger className="h-8 rounded-xl text-xs border-slate-200 shadow-sm">
              <SelectValue placeholder="Filtrar por usuario" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sem filtro</SelectItem>
              {listaUsuariosTransferencia.map((option) => (
                <SelectItem key={option._id} value={option.nomeFantasia}>
                  {option.firstName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2 h-8 shadow-sm">
          <Input
            className="border-0 h-7 text-xs shadow-none focus-visible:ring-0 w-[160px]"
            placeholder="Pesquisar..."
            value={filterPesquisa}
            onChange={(e) => setFilterPesquisa(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onPesquisar()}
          />
          {isPesquisado && (
            <button onClick={() => { setIsPesquisado(false); setFilterPesquisa(''); }} className="p-1 text-gray-400 hover:text-gray-600">
              <Iconify width={14} icon="ooui:clear" />
            </button>
          )}
          <button onClick={onPesquisar} className="p-1 text-gray-400 hover:text-violet-600">
            <Iconify icon="eva:search-outline" width={16} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={D.lista.container}>
        <div className={D.lista.card}>
          <div className={D.lista.statusTabs.container}>
            <Tabs value={filterStatus} onValueChange={onChangeFilterStatus}>
              <TabsList className={D.lista.statusTabs.list}>
                {STATUS_OPTIONS.map((tab) => (
                  <TabsTrigger key={String(tab.value)} value={tab.value}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <div className="flex-1 overflow-auto">
            <GenericTable headLabels={TABLE_HEAD}>
              {dataFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <ChamadosRowTableNew
                  key={row._id || row.id}
                  row={row}
                  onViewRow={() => handleOpenDetails(row)}
                />
              ))}
              {dataFiltered.length === 0 && (
                <div className="px-6 py-12 text-center text-sm text-gray-500">Nenhum chamado encontrado</div>
              )}
            </GenericTable>
          </div>

          <div className={D.lista.pagination.container}>
            <div className={D.lista.pagination.left}>
              <Label className={D.lista.pagination.label}>Linhas por pagina:</Label>
              <Select value={String(rowsPerPage)} onValueChange={(value) => onChangeRowsPerPage({ target: { value: String(value) } })}>
                <SelectTrigger className="w-[70px] h-8 text-xs rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[25, 50, 100].map((option) => (
                    <SelectItem key={option} value={String(option)}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className={D.lista.pagination.info}>
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, dataFiltered.length)} de {dataFiltered.length}
              </span>
            </div>
            <div className={D.lista.pagination.right}>
              <Button variant="outline" size="sm" onClick={() => onChangePage(null, page - 1)} disabled={page === 0}>
                Anterior
              </Button>
              <Button variant="outline" size="sm" onClick={() => onChangePage(null, page + 1)} disabled={page >= Math.ceil(dataFiltered.length / rowsPerPage) - 1}>
                Proxima
              </Button>
            </div>
          </div>
        </div>
      </div>

      <NovoChamado {...novoChamadoProps} />
    </div>
  );
}

function applySortFilter({ tableData, filterStatus, valorEscolhidoUsuario }) {
  if (filterStatus !== null) {
    tableData = tableData.filter((item) => item.status === filterStatus);
  }
  if (valorEscolhidoUsuario) {
    tableData = tableData.filter((item) => item.nome_autor === valorEscolhidoUsuario);
  }
  return tableData;
}
